import React from 'react';
import { Flex } from 'antd';
import { SbTooltip, SbInfoTooltip, SbForm, CloseOutlined, TooltipTriggerStyled } from 'uiCore';
import SbUpload from 'uiCore/SbUpload/SbUpload';
import SbInput from 'uiCore/SbInput/SbInput';
import SbSelect from 'uiCore/SbSelect/SbSelect';
import SbSwitch from 'uiCore/SbSwitch/SbSwitch';
import SbButton from 'uiCore/SbButton/SbButton';
import SbRow from 'uiCore/SbRow/SbRow';
import SbCol from 'uiCore/SbCol/SbCol';
import SbDrawer from 'uiCore/SbDrawer/SbDrawer';
import Permission from 'Components/Permission';
import ErrorMessageBox from 'Components/Common/ErrorMessageBox';
import ExternalJDFileIcon from 'Components/SBLayoutIcon/ExternalJDFileIcon';
import { SbFormItemStyled, FixedHeight, UploadedFilePillStyled, UploadedFileNameStyled } from './SbJdCreationDrawerV2.styles';
import useJdForm from './useJdForm';
import { JD_ACTION } from 'constants/jobs.constants';
import { SbSpin } from 'uiCore';

// ── Tooltip content strings (PRD §5.7–5.8) ───────────────────────────────────
const TOOLTIPS = {
    jobCode:           "Select the job code this JD belongs to. Drives the org hierarchy dropdowns and is used for skills mapping and PAIC.",
    standardIndustry:  "The canonical name for this role in your industry (e.g. 'Software Engineer'). Used for skills benchmarking and PAIC analysis. AI suggests a value — you can override.",
    jobTitleForJd:     "The exact job title that will appear in the published JD. Avoid abbreviations or acronyms — this is candidate-facing.",
    businessUnit:      "The business unit this role belongs to. Filters the Function dropdown to show only relevant options.",
    function:          "The organisational function (e.g. Engineering, Finance, Marketing). Filtered by the selected Business Unit.",
    subFunction:       "The sub-division within the function (e.g. Backend, FP&A, Brand). Filtered by the selected Function.",
    specialty:        "The specialty within the sub-function (e.g. Full-Stack, DevOps, Data Science). Filtered by the selected Sub-function.",
    roleType:          "Select whether this is a Technical, Managerial, Support, or other role type. This influences JD tone and responsibilities.",
    jobGrade:          "The seniority level (e.g. L3 — Senior Specialist). Used for compensation modeling and AI calibration.",
    workEnvironment:   "Select whether this role is On-site, Hybrid, or Remote. Influences JD language and location requirements.",
    location:          "Select the office location(s) for this role. Available options are limited to locations linked to the selected Legal Entity.",
    roleContext:       "Optional but recommended. Add specific nuances for this role: e.g. 'Leads a team of 6 in APAC', 'Qualitative research focus', 'Will own performance review cycle'. The AI uses this to tailor the JD.",
    jdUpload:          "Upload the existing JD for this role (PDF or DOCX, max 10MB). The AI will analyse the document and extract structured data.",
    roleReportingTo:   "Select the role this JD reports to. Only one of Role Reporting To or Is Apex can be set — selecting a role clears Is Apex.",
    isApex:            "Mark this role as an apex role (top of the org hierarchy, reports to no one). Only one of Role Reporting To or Is Apex can be set — enabling this clears Role Reporting To.",
    submit:            "Submit the form to begin AI generation. The JD will be ready in approximately 4–5 minutes. You can navigate away — you'll be notified when it's done.",
};

// ── FieldWrapper: visibility guard + uniform Form.Item wrapping ───────────────

const FieldWrapper = ({ cfg, name, children, ...rest }) => {
    if (!cfg?.visible) return null;
    return (
        <SbFormItemStyled
            name={name}
            label={cfg.label}
            rules={cfg.required ? [{ required: true, message: `${cfg.label} is required` }] : []}
            {...rest}
        >
            {children}
        </SbFormItemStyled>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const SbJdCreationDrawerV2 = (props) => {
    const {
        jdActionSelection = JD_ACTION.CREATE,
        isDrawerOpen,
        toggleDrawer,
        drawerSubmitLoading,
        selectedJdVersion,
    } = props;

    const {
        form,
        loading,
        fields,
        legalEntity,
        businessUnit,
        functionDepartment,
        subFunction,
        specialty,
        roleType,
        jobGrade,
        workEnvironment,
        jobCode,
        roleReportingToSbUniqueId,
        isApexRole,
        jobOptions,
        roleTypeOptions,
        jobGradeOptions,
        workEnvironmentOptions,
        legalEntities,
        reportingToOptions,
        roleReportingToOptions,
        availableBusinessUnits,
        availableFunctions,
        availableSubFunctions,
        availableSpecialties,
        filteredOfficeLocations,
        versionOptions,
        isFieldDisabled,
        handleJobChange,
        handleVersionChangeInput,
        onFinish,
        uploadedFile,
        uploadError,
        handleBeforeUpload,
        handleRemoveUpload,
        handlePreviewUpload,
        uploadSubmitting,
    } = useJdForm(props);

    const isRevise = jdActionSelection === JD_ACTION.REVISE;
    const isUpload = jdActionSelection === JD_ACTION.UPLOAD;

    return (
        <SbDrawer
            title={isUpload ? 'External JD Upload' : isRevise ? 'Revise JD' : 'Create JD'}
            placement="right"
            width="45vw"
            open={isDrawerOpen}
            onClose={toggleDrawer}
            footer={
                <Flex justify="flex-end">
                    {loading ? (
                        <SbSpin size="small" />
                    ) : (
                        <SbTooltip content={TOOLTIPS.submit}>
                            <TooltipTriggerStyled>
                                <SbButton
                                    buttonType="primary"
                                    loading={drawerSubmitLoading}
                                    disabled={loading || drawerSubmitLoading || (isUpload && uploadSubmitting)}
                                    onClick={() => form.submit()}
                                >
                                    {isUpload ? 'Upload Job Description' : 'Submit'}
                                </SbButton>
                            </TooltipTriggerStyled>
                        </SbTooltip>
                    )}
                </Flex>
            }
        >
            <SbForm
                form={form}
                layout="vertical"
                onFinish={onFinish}
                colon={false}
            >
                {/* ── Version select (revise mode) ── */}
                {isRevise && (
                    <SbFormItemStyled label="Job Description Version">
                        <SbSelect
                            value={selectedJdVersion ? String(selectedJdVersion) : undefined}
                            options={versionOptions}
                            onChange={handleVersionChangeInput}
                        />
                    </SbFormItemStyled>
                )}

                {/* ── Job Code / Role Title fallback ── */}
                {fields.jobCode?.visible && (
                    jobOptions.length > 0 ? (
                        <SbTooltip content={TOOLTIPS.jobCode}>
                            <TooltipTriggerStyled>
                                <FieldWrapper cfg={fields.jobCode} name="jobCode">
                                    <SbSelect
                                        clearStaleValue={false}
                                        showSearch={{ optionFilterProp: 'label'}}
                                        options={jobOptions}
                                        placeholder={`Select ${fields.jobCode?.placeholder || fields.jobCode?.label || 'Job Code'}`}
                                        disabled={isFieldDisabled('jobCode')}
                                        allowClear
                                        onChange={handleJobChange}
                                    />
                                </FieldWrapper>
                            </TooltipTriggerStyled>
                        </SbTooltip>
                    ) : (
                        <SbTooltip content={TOOLTIPS.jobCode}>
                            <TooltipTriggerStyled>
                                <FieldWrapper cfg={fields.jobCode} name="roleTitle">
                                    <SbInput
                                        placeholder={`Enter ${fields.jobCode?.placeholder || fields.jobCode?.label || 'Role Title'}`}
                                    />
                                </FieldWrapper>
                            </TooltipTriggerStyled>
                        </SbTooltip>
                    )
                )}

                {/* ── Alt Role Title (Standard Industry Name) ── */}
                {fields.altRoleTitle?.visible && (
                    <SbFormItemStyled
                        name="altRoleTitle"
                        label={
                            <SbInfoTooltip content={TOOLTIPS.standardIndustry}>
                                {fields.altRoleTitle.label}
                            </SbInfoTooltip>
                        }
                        rules={fields.altRoleTitle.required ? [{ required: true, message: `${fields.altRoleTitle.label} is required` }] : []}
                    >
                        <SbInput
                            placeholder={fields.altRoleTitle?.placeholder || fields.altRoleTitle?.label}
                            disabled
                        />
                    </SbFormItemStyled>
                )}

                {/* ── Job Title for JD ── */}
                <SbTooltip content={TOOLTIPS.jobTitleForJd}>
                    <TooltipTriggerStyled>
                        <FieldWrapper cfg={fields.jobTitleForJd} name="jobTitleForJd">
                            <SbInput
                                placeholder={fields.jobTitleForJd?.placeholder || ''}
                                disabled={isFieldDisabled('jobTitleForJd')}
                            />
                        </FieldWrapper>
                    </TooltipTriggerStyled>
                </SbTooltip>

                {/* ── Legal Entity + Business Unit ── */}
                {(fields.legalEntity?.visible || fields.businessUnit?.visible) && (
                    <SbRow gutter={16}>
                        {fields.legalEntity?.visible && (
                            <SbCol span={fields.businessUnit?.visible ? 12 : 24}>
                                <SbFormItemStyled
                                    name="legalEntity"
                                    label={fields.legalEntity.label}
                                    rules={fields.legalEntity.required ? [{ required: true, message: `${fields.legalEntity.label} is required` }] : []}
                                >
                                    <SbSelect      
                                        clearStaleValue={false}
                                        options={legalEntities}
                                        placeholder={`Select ${fields.legalEntity.label}`}
                                        disabled={!!legalEntity}
                                    />
                                </SbFormItemStyled>
                            </SbCol>
                        )}
                        {fields.businessUnit?.visible && (
                            <SbCol span={fields.legalEntity?.visible ? 12 : 24}>
                                <SbTooltip content={TOOLTIPS.businessUnit}>
                                    <TooltipTriggerStyled>
                                        <SbFormItemStyled
                                            name="businessUnit"
                                            label={fields.businessUnit.label}
                                            rules={fields.businessUnit.required ? [{ required: true, message: `${fields.businessUnit.label} is required` }] : []}
                                        >
                                            <SbSelect
                                                clearStaleValue={false}
                                                options={availableBusinessUnits}
                                                placeholder={`Select ${fields.businessUnit.label}`}
                                                disabled={!!legalEntity}
                                            />
                                        </SbFormItemStyled>
                                    </TooltipTriggerStyled>
                                </SbTooltip>
                            </SbCol>
                        )}
                    </SbRow>
                )}

                {/* ── Function + Sub-function ── */}
                {(fields.function?.visible || fields.subFunction?.visible) && (
                    <SbRow gutter={16}>
                        {fields.function?.visible && (
                            <SbCol span={fields.subFunction?.visible ? 12 : 24}>
                                <SbTooltip content={TOOLTIPS.function}>
                                    <TooltipTriggerStyled>
                                        <SbFormItemStyled
                                            name="functionDepartment"
                                            label={fields.function.label}
                                            rules={fields.function.required ? [{ required: true, message: `${fields.function.label} is required` }] : []}
                                        >
                                            <SbSelect
                                                clearStaleValue={false}
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                allowClear
                                                options={availableFunctions}
                                                placeholder={`Select ${fields.function.label}`}
                                                disabled={!businessUnit || isFieldDisabled('functionDepartment')}
                                            />
                                        </SbFormItemStyled>
                                    </TooltipTriggerStyled>
                                </SbTooltip>
                            </SbCol>
                        )}
                        {fields.subFunction?.visible && (
                            <SbCol span={fields.function?.visible ? 12 : 24}>
                                <SbTooltip content={TOOLTIPS.subFunction}>
                                    <TooltipTriggerStyled>
                                        <SbFormItemStyled
                                            name="subFunction"
                                            label={fields.subFunction.label}
                                            rules={fields.subFunction.required ? [{ required: true, message: `${fields.subFunction.label} is required` }] : []}
                                        >
                                            <SbSelect
                                                clearStaleValue={false}
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                allowClear
                                                options={availableSubFunctions}
                                                placeholder={`Select ${fields.subFunction.label}`}
                                                disabled={!functionDepartment?.length || (isRevise && Boolean(subFunction?.length))}
                                            />
                                        </SbFormItemStyled>
                                    </TooltipTriggerStyled>
                                </SbTooltip>
                            </SbCol>
                        )}
                    </SbRow>
                )}
                {fields.specialty?.visible && (
                    <SbTooltip content={TOOLTIPS.specialty}>
                        <TooltipTriggerStyled>
                            <SbFormItemStyled
                                name="specialty"
                                label={fields.specialty?.label || 'Specialty'}
                                rules={fields.specialty?.required ? [{ required: true, message: `${fields.specialty?.label || 'Specialty'} is required` }] : []}
                            >
                                <SbSelect
                                    clearStaleValue={false}
                                    mode="multiple"
                                    maxTagCount="responsive"
                                    options={availableSpecialties}
                                    placeholder={`Select ${fields.specialty?.label || 'Specialty'}`}
                                    allowClear
                                    disabled={!subFunction?.length || (isRevise && Boolean(specialty?.length))}
                                />
                            </SbFormItemStyled>
                        </TooltipTriggerStyled>
                    </SbTooltip>
                )}

                {/* ── Role Type + Job Grade ── */}
                {(fields.roleType?.visible || fields.level?.visible) && (
                    <SbRow gutter={16}>
                        {fields.roleType?.visible && (
                            <SbCol span={fields.level?.visible ? 12 : 24}>
                                <SbTooltip content={TOOLTIPS.roleType}>
                                    <TooltipTriggerStyled>
                                        <SbFormItemStyled
                                            name="roleType"
                                            label={fields.roleType.label}
                                            rules={fields.roleType.required ? [{ required: true, message: `${fields.roleType.label} is required` }] : []}
                                        >
                                            <SbSelect
                                                clearStaleValue={false}
                                                options={roleTypeOptions}
                                                placeholder={`Select ${fields.roleType.label}`}
                                                disabled={!!roleType}
                                            />
                                        </SbFormItemStyled>
                                    </TooltipTriggerStyled>
                                </SbTooltip>
                            </SbCol>
                        )}
                        {fields.level?.visible && (
                            <SbCol span={fields.roleType?.visible ? 12 : 24}>
                                <SbTooltip content={TOOLTIPS.jobGrade}>
                                    <TooltipTriggerStyled>
                                        <SbFormItemStyled
                                            name="jobGrade"
                                            label={fields.level.label}
                                            rules={fields.level.required ? [{ required: true, message: `${fields.level.label} is required` }] : []}
                                        >
                                            <SbSelect
                                                clearStaleValue={false}
                                                options={jobGradeOptions}
                                                placeholder={`Select ${fields.level.label}`}
                                                disabled={!!jobGrade}
                                            />
                                        </SbFormItemStyled>
                                    </TooltipTriggerStyled>
                                </SbTooltip>
                            </SbCol>
                        )}
                    </SbRow>
                )}

                {/* ── Reporting To ── */}
                <FieldWrapper cfg={fields.reportingTo} name="reportingTo">
                    <SbSelect
                        clearStaleValue={false}
                        options={reportingToOptions}
                        placeholder={`Select ${fields.reportingTo?.label}`}
                        size="large"
                        disabled={isFieldDisabled('reportingTo')}
                    />
                </FieldWrapper>

                {/* ── Work Environment + Location ── */}
                {fields.workEnvironment?.visible && (
                    <SbRow gutter={16}>
                        <SbCol span={12}>
                            <SbTooltip content={TOOLTIPS.workEnvironment}>
                                <TooltipTriggerStyled>
                                    <SbFormItemStyled
                                        name="workEnvironment"
                                        label={fields.workEnvironment.label}
                                        rules={fields.workEnvironment.required ? [{ required: true, message: `${fields.workEnvironment.label} is required` }] : []}
                                    >
                                        <SbSelect
                                            clearStaleValue={false}
                                            options={workEnvironmentOptions}
                                            placeholder={`Select ${fields.workEnvironment.label}`}
                                        />
                                    </SbFormItemStyled>
                                </TooltipTriggerStyled>
                            </SbTooltip>
                        </SbCol>
                        <SbCol span={12}>
                            <SbTooltip content={TOOLTIPS.location}>
                                <TooltipTriggerStyled>
                                    <SbFormItemStyled
                                        name="location"
                                        label={fields.location?.label || 'Location'}
                                        rules={[{ required: fields.location?.required, message: 'Location is required' }]}
                                    >
                                        <FixedHeight
                                            clearStaleValue={false}
                                            options={filteredOfficeLocations}
                                            placeholder="Select location(s)..."
                                            mode={workEnvironment !== 'Remote' ? 'multiple' : undefined}
                                            disabled={!jobCode || isFieldDisabled('location')}
                                            onMultiModeChange={(vals) => form.setFieldValue('location', vals)}
                                        />
                                    </SbFormItemStyled>
                                </TooltipTriggerStyled>
                            </SbTooltip>
                        </SbCol>
                    </SbRow>
                )}

                {!isRevise && (
                    <SbRow gutter={16}>
                        <SbCol span={16}>
                            <SbTooltip content={TOOLTIPS.roleReportingTo}>
                                <TooltipTriggerStyled>
                                    <SbFormItemStyled name="roleReportingToSbUniqueId" label="Role Reporting To">
                                        <SbSelect
                                            clearStaleValue={false}
                                            showSearch={{ optionFilterProp: 'label' }}
                                            options={roleReportingToOptions}
                                            placeholder="Select Role Reporting To"
                                            allowClear
                                            disabled={isApexRole}
                                        />
                                    </SbFormItemStyled>
                                </TooltipTriggerStyled>
                            </SbTooltip>
                        </SbCol>
                        <SbCol span={8}>
                            <SbTooltip content={TOOLTIPS.isApex}>
                                <TooltipTriggerStyled>
                                    <SbFormItemStyled name="isApexRole" label="Is Apex" valuePropName="checked">
                                        <SbSwitch disabled={!!roleReportingToSbUniqueId} />
                                    </SbFormItemStyled>
                                </TooltipTriggerStyled>
                            </SbTooltip>
                        </SbCol>
                    </SbRow>
                )}

                {/* ── JD Document (upload mode) or Role Context (create/revise mode) ── */}
                {isUpload ? (
                    <SbFormItemStyled
                        label="Attach JD Document *"
                        required
                        rules={[{ required: true, message: 'Please attach a JD document.' }]}
                    >
                        <SbTooltip content={TOOLTIPS.jdUpload}>
                            <TooltipTriggerStyled>
                                <SbUpload
                                    type="drag"
                                    accept=".pdf,.docx"
                                    maxCount={1}
                                    fileList={[]}
                                    showUploadList={false}
                                    beforeUpload={handleBeforeUpload}
                                    disabled={!!uploadedFile}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <ExternalJDFileIcon />
                                    </p>
                                    <p className="ant-upload-text">Drag &amp; drop your JD or browse</p>
                                    <p className="ant-upload-hint">PDF or Word (.pdf, .docx) · max 10MB</p>
                                </SbUpload>
                            </TooltipTriggerStyled>
                        </SbTooltip>
                        {uploadedFile && (
                            <UploadedFilePillStyled align="center" justify="space-between" gap={16}>
                                <UploadedFileNameStyled>
                                    {uploadedFile.name}
                                </UploadedFileNameStyled>
                                <Flex align="center" gap={8}>
                                    <SbButton buttonType="action" size="small" onClick={handlePreviewUpload}>
                                        Preview file
                                    </SbButton>
                                    <SbTooltip content="Remove file">
                                        <TooltipTriggerStyled>
                                            <SbButton
                                                buttonType="action"
                                                size="small"
                                                icon={<CloseOutlined />}
                                                onClick={handleRemoveUpload}
                                            />
                                        </TooltipTriggerStyled>
                                    </SbTooltip>
                                </Flex>
                            </UploadedFilePillStyled>
                        )}
                        {uploadError && (
                            <ErrorMessageBox message={uploadError} />
                        )}
                    </SbFormItemStyled>
                ) : null}

                {!isUpload && (
                    <Permission resource="jobs_library" actions={['prompt']}>
                        <SbFormItemStyled
                            name="roleContext"
                            label={
                                <SbInfoTooltip content={TOOLTIPS.roleContext}>
                                    {`${fields.roleContext?.label || 'Role Context'}${fields.roleContext?.required ? ' *' : ''}`}
                                </SbInfoTooltip>
                            }
                            rules={[{ required: fields.roleContext?.required, message: `${fields.roleContext?.label || 'Role Context'} is required` }]}
                        >
                            <SbInput
                                textarea
                                rows={5}
                                placeholder={
                                    "If necessary add an optional role context to help fine-tune the job description, e.g.,\n" +
                                    "1. Research role - qualitative or quantitative research role\n" +
                                    "2. HR role - will lead performance reviews\n" +
                                    "3. Analytics role - requires experience in XYZ analytical tools"
                                }
                            />
                        </SbFormItemStyled>
                    </Permission>
                )}
            </SbForm>
        </SbDrawer>
    );
};

export default SbJdCreationDrawerV2;